import {
    AlertTriangle,
    CheckCircle2,
    FileText,
    Loader2,
    Lock,
    RefreshCw,
    Trash2,
} from 'lucide-react'
import { StatCard } from '~/components/StatCard'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { BulkActionBar } from '~/components/ui/bulk-action-bar'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Checkbox } from '~/components/ui/checkbox'
import { EmptyState } from '~/components/ui/empty-state'
import { SearchInput } from '~/components/ui/search-input'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import {
    ChartCard,
    CategoryBarChart,
    KRDS,
    type CategoryDatum,
} from '~/components/charts'
import type { TableSelection } from '~/lib/use-table-selection'
import { formatDateTime } from '~/lib/utils'

// 업로드 파이프라인의 documents 한 행. (KnowledgePage·UploadManagementSection·상세 패널 공용)
export interface DocumentRow {
    id: string
    title: string
    domain: string
    status: string
    retryCount: number
    // 적재 시각(백엔드가 응답에 추가). "언제" 노출용.
    createdAt?: string | null
    // 실패 사유(status=failed일 때). 상세 패널에서 노출.
    error?: string | null
    // 오브젝트 스토리지 원본 key. 상세 패널의 원본 파일명·스토리지 키 표시에 쓴다.
    sourceKey?: string
    // 업로드 시 저장한 원본 파일명(filename)·content_type 등. 상세 패널 표시에 쓴다.
    metadata?: Record<string, string>
    // 수동 관리 잠금 여부. true면 재색인이 이 문서를 건너뛴다(잠금 토글·재색인 차단 반영).
    curationLocked?: boolean
}

// 문서 상태 머신: pending → parsing → chunking → embedding → indexing → completed (실패 시 failed).
// (상세 패널과 공유하므로 톤·라벨 맵을 export 한다.)
export const STATUS_TONE: Record<string, BadgeTone> = {
    pending: 'neutral',
    parsing: 'info',
    chunking: 'info',
    embedding: 'warning',
    indexing: 'warning',
    completed: 'success',
    failed: 'danger',
}

// 한국어 라벨 — 진행 단계는 사람이 읽기 좋게.
export const STATUS_LABEL: Record<string, string> = {
    pending: '대기',
    parsing: '파싱',
    chunking: '청크 분할',
    embedding: '임베딩',
    indexing: '색인',
    completed: '완료',
    failed: '실패',
}

// 차트 의미색 — 진행=info, 임베딩/색인=warning, 완료=success, 실패=danger, 대기=중립.
// (recharts는 hex가 필요하므로 chart-colors의 KRDS 토큰 미러를 쓴다.)
const STATUS_FILL: Record<string, string> = {
    pending: KRDS.gray[40],
    parsing: KRDS.info,
    chunking: KRDS.info,
    embedding: KRDS.warning,
    indexing: KRDS.warning,
    completed: KRDS.success,
    failed: KRDS.danger,
}

// 카운트 표시 순서(상태 머신 진행 순).
const STATUS_ORDER = [
    'pending',
    'parsing',
    'chunking',
    'embedding',
    'indexing',
    'completed',
    'failed',
]

// 진행 중(완료·실패·대기를 제외한 처리 단계)으로 간주할 상태.
const IN_PROGRESS = new Set(['parsing', 'chunking', 'embedding', 'indexing'])

// ───────── 업로드 관리(Postgres documents) — 적재/재색인/삭제 파이프라인 ─────────
// 인덱싱 상태 KPI·분포 차트 + 검색(제목·도메인 ILIKE) + 다중선택 일괄작업(재색인/삭제) +
// documents 표(행별 재색인/삭제). 운영 CLI로 적재된 코퍼스는 위 ‘색인된 코퍼스’에 표시된다.
export function UploadManagementSection({
    docs,
    loading,
    q,
    onQChange,
    selection,
    bulkBusy,
    onReindex,
    onDelete,
    onSelect,
    onBulkReindex,
    onRequestBulkDelete,
}: {
    docs: DocumentRow[]
    loading: boolean
    q: string
    onQChange: (value: string) => void
    selection: TableSelection<string>
    bulkBusy: boolean
    onReindex: (id: string) => void
    onDelete: (doc: DocumentRow) => void
    onSelect: (doc: DocumentRow) => void
    onBulkReindex: () => void
    onRequestBulkDelete: () => void
}) {
    // 상태별 카운트(있는 상태만 진행 순으로).
    const counts = docs.reduce<Record<string, number>>((acc, d) => {
        acc[d.status] = (acc[d.status] ?? 0) + 1
        return acc
    }, {})

    // KPI 집계: 전체 / 완료 / 실패 / 진행 중.
    const total = docs.length
    const completed = counts.completed ?? 0
    const failed = counts.failed ?? 0
    const inProgress = STATUS_ORDER.filter((s) => IN_PROGRESS.has(s)).reduce(
        (s, k) => s + (counts[k] ?? 0),
        0
    )

    // 상태 분포 막대(있는 상태만, 진행 순·의미색).
    const statusData: CategoryDatum[] = STATUS_ORDER.filter(
        (s) => counts[s] != null
    ).map((s) => ({
        name: STATUS_LABEL[s] ?? s,
        value: counts[s],
        color: STATUS_FILL[s] ?? KRDS.gray[40],
    }))

    // 현재 보이는 행 id — 전체선택 체크박스 상태/토글 기준.
    const visibleIds = docs.map((d) => d.id)
    const headState = selection.allState(visibleIds)

    return (
        <section>
            <div className='mb-3'>
                <div className='admin-eyebrow'>업로드 관리</div>
                <p className='text-muted-foreground mt-0.5 text-[13px]'>
                    문서 적재 파이프라인의 인덱싱 상태 · 재색인 · 삭제 · 운영
                    CLI로 적재된 코퍼스는 위 ‘색인된 코퍼스’에 표시됩니다.
                </p>
            </div>

            {loading ? (
                <div className='space-y-4'>
                    <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className='h-20' />
                        ))}
                    </div>
                    <Skeleton className='h-56' />
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className='h-9' />
                    ))}
                </div>
            ) : docs.length === 0 && !q.trim() ? (
                <Card>
                    <EmptyState
                        icon={FileText}
                        title='업로드된 문서 없음'
                        description='업로드 파이프라인에 적재된 문서가 없어요. 운영 CLI로 적재된 RAG 코퍼스는 위 ‘색인된 코퍼스’에 표시됩니다.'
                    />
                </Card>
            ) : (
                <>
                    {/* 검색어가 0건이면 KPI·차트는 감추고 표의 빈 상태만 노출한다. */}
                    {docs.length > 0 ? (
                        <>
                            {/* 상단 KPI: 전체·완료·실패·진행 중 */}
                            <div className='mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4'>
                                <StatCard
                                    label='총 문서'
                                    value={total}
                                    icon={FileText}
                                />
                                <StatCard
                                    label='완료'
                                    value={completed}
                                    icon={CheckCircle2}
                                    tone='success'
                                    hint='색인 완료'
                                />
                                <StatCard
                                    label='진행 중'
                                    value={inProgress}
                                    icon={Loader2}
                                    tone='info'
                                    hint='파싱·청크·임베딩·색인'
                                />
                                <StatCard
                                    label='실패'
                                    value={failed}
                                    icon={AlertTriangle}
                                    tone='danger'
                                    hint='재색인 필요'
                                />
                            </div>

                            {/* 상태별 처리 현황(상태 머신 진행 순 막대) */}
                            <div className='mb-4'>
                                <ChartCard
                                    title='인덱싱 상태 분포'
                                    description={`대기 → 파싱 → 청크 → 임베딩 → 색인 → 완료 · 전체 ${total}건`}>
                                    <CategoryBarChart
                                        data={statusData}
                                        layout='horizontal'
                                    />
                                </ChartCard>
                            </div>
                        </>
                    ) : null}

                    {/* 검색: 제목·도메인 ILIKE */}
                    <div className='mb-3'>
                        <SearchInput
                            className='w-72'
                            value={q}
                            onChange={onQChange}
                            placeholder='제목·도메인 검색'
                        />
                    </div>

                    {/* 선택 시 떠오르는 일괄작업 바 — 재색인은 즉시, 삭제는 확인을 거친다. */}
                    <div className='mb-3'>
                        <BulkActionBar
                            count={selection.count}
                            onClear={selection.clear}
                            sticky
                            position='top'>
                            <Button
                                variant='outline'
                                size='sm'
                                disabled={bulkBusy}
                                onClick={onBulkReindex}>
                                <RefreshCw />
                                재색인
                            </Button>
                            <Button
                                variant='outline'
                                size='sm'
                                disabled={bulkBusy}
                                onClick={onRequestBulkDelete}
                                className='text-destructive hover:text-destructive'>
                                <Trash2 />
                                삭제
                            </Button>
                        </BulkActionBar>
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className='w-9'>
                                    <Checkbox
                                        aria-label='현재 목록 전체 선택'
                                        checked={headState === 'all'}
                                        indeterminate={headState === 'some'}
                                        disabled={visibleIds.length === 0}
                                        onChange={() =>
                                            selection.toggleAll(visibleIds)
                                        }
                                    />
                                </TableHead>
                                <TableHead>제목</TableHead>
                                <TableHead>도메인</TableHead>
                                <TableHead>상태</TableHead>
                                <TableHead>재시도</TableHead>
                                <TableHead>생성일</TableHead>
                                <TableHead>작업</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {docs.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className='text-muted-foreground py-8 text-center'>
                                        등록된 문서가 없어요.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                docs.map((d) => (
                                    // 행 클릭 → 원본 파일 정보·다운로드 상세 패널. 체크박스·작업 버튼은
                                    // stopPropagation으로 행 클릭과 분리한다.
                                    <TableRow
                                        key={d.id}
                                        className='cursor-pointer'
                                        onClick={() => onSelect(d)}>
                                        {/* 체크박스 클릭은 행 동작과 분리한다(stopPropagation). */}
                                        <TableCell
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }>
                                            <Checkbox
                                                aria-label={`${d.title} 선택`}
                                                checked={selection.isSelected(
                                                    d.id
                                                )}
                                                onChange={() =>
                                                    selection.toggle(d.id)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{d.title}</TableCell>
                                        <TableCell>{d.domain}</TableCell>
                                        <TableCell>
                                            <span className='flex items-center gap-1.5'>
                                                <Badge
                                                    tone={
                                                        STATUS_TONE[d.status] ??
                                                        'neutral'
                                                    }>
                                                    {STATUS_LABEL[d.status] ??
                                                        d.status}
                                                </Badge>
                                                {d.curationLocked ? (
                                                    <Badge
                                                        tone='warning'
                                                        title='수동 관리 잠금 · 재색인이 이 문서를 건너뜁니다'>
                                                        <Lock className='size-3' />
                                                        잠금
                                                    </Badge>
                                                ) : null}
                                            </span>
                                        </TableCell>
                                        <TableCell>{d.retryCount}</TableCell>
                                        <TableCell className='whitespace-nowrap'>
                                            {formatDateTime(d.createdAt)}
                                        </TableCell>
                                        {/* 작업 버튼은 행 클릭(상세 패널)과 분리한다(stopPropagation). */}
                                        <TableCell
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }>
                                            <span className='flex gap-1'>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    disabled={d.curationLocked}
                                                    title={
                                                        d.curationLocked
                                                            ? '수동 관리 잠금 상태예요. 재색인하려면 먼저 잠금을 해제하세요.'
                                                            : undefined
                                                    }
                                                    onClick={() =>
                                                        onReindex(d.id)
                                                    }>
                                                    재색인
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={() => onDelete(d)}
                                                    className='text-destructive hover:text-destructive'>
                                                    삭제
                                                </Button>
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </>
            )}
        </section>
    )
}
